// IDs come from the data attributes rendered by
// snippets/package-protection-block.liquid, which resolves them from the
// uh_protection.config shop metafield. There is deliberately no fallback:
// if the attributes are missing we fail closed rather than silently
// targeting the wrong store's variant.

class PackageProtectionComponent extends HTMLElement {
  constructor() {
    super();

    this.productId = parseInt(this.dataset.productId, 10) || 0;
    this.variantId = parseInt(this.dataset.variantId, 10) || 0;
    this.cartDrawer = document.querySelector("cart-drawer");
    this.mainCart = document.querySelector("cart-items");
    this.checkbox = this.querySelector(".f-package-protection__checkbox");
    this.card = this.querySelector(".f-package-protection");
    this.loadingEl = this.querySelector(".f-package-protection__loading");
    this.priceEl = this.querySelector("[data-package-protection-price]");
    this.isLoading = false;
    this.cartUpdateUnsubscriber = undefined;
  }

  connectedCallback() {
    if (!this.productId || !this.variantId) {
      console.error(
        "[UH-PP] Missing data-product-id / data-variant-id — protection disabled."
      );
      this.hidden = true;
      return;
    }

    this.checkbox.addEventListener("change", this.onCheckboxChange.bind(this));

    this.cartUpdateUnsubscriber = window.FoxThemeEvents.subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (cart) => this.syncFromCart(cart)
    );

    // After a real add/remove/qty change, if no PROTECTABLE item
    // remains (cart empty OR only non-refundable/discount items),
    // remove protection. Uses the document "cart:updated" event
    // (fires only after a completed cart op, not on modal renders)
    // and defers the mutation past the render cycle.
    this._onCartUpdatedDoc = (e) => {
      const cart = e && e.detail && e.detail.cart;
      if (!cart) return;
      setTimeout(() => {
        // A protectable item may have just been added (e.g. after
        // a non-refundable one) — try auto-add; and if none remain,
        // remove protection. Order: add-eligibility first, then
        // cleanup (they are mutually exclusive by condition).
        this.autoAddIfFirstTime(cart);
        this.removeProtectionIfNoProtectable(cart);
      }, 0);
    };
    document.addEventListener('cart:updated', this._onCartUpdatedDoc);

    FoxThemeCartHelpers.getState().then((cart) => {
      this.syncFromCart(cart);
      this.upgradeProtectionIfNeeded(cart);
      this.autoAddIfFirstTime(cart);
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this._onCartUpdatedDoc) {
      document.removeEventListener('cart:updated', this._onCartUpdatedDoc);
      this._onCartUpdatedDoc = null;
    }
  }

  findAllProtectionItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];

    return cart.items.filter((item) => item.product_id === this.productId);
  }

  hasTargetVariant(cart) {
    return this.findAllProtectionItems(cart).some(
      (item) => item.variant_id === this.variantId
    );
  }

  syncFromCart(cart) {
    const protectionItems = this.findAllProtectionItems(cart);
    this.checkbox.checked = protectionItems.length > 0;
  }

  async upgradeProtectionIfNeeded(cart) {
    if (this.isLoading) return;

    const protectionItems = this.findAllProtectionItems(cart);
    if (protectionItems.length === 0 || this.hasTargetVariant(cart)) return;

    await this.replaceWithTargetVariant();
  }

  // A "protectable" item is a real product (not the protection
  // product) whose title/variant is NOT non-refundable or
  // discount. Shared by auto-add and empty-cart cleanup.
  countProtectableItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    const NON_PROTECTABLE = [
      'nonrefundable', 'non-refundable', 'non refundable',
      'non-returnable', 'non returnable', 'discount',
    ];
    return cart.items.filter((item) => {
      if (item.product_id === this.productId) return false;
      const t = (
        (item.title || '') + ' ' + (item.variant_title || '')
      ).toLowerCase();
      return !NON_PROTECTABLE.some((kw) => t.includes(kw));
    }).length;
  }

  // Remove protection when no protectable item remains (cart
  // empty, or only non-refundable/discount items left). Automatic
  // removal: does not set the "user removed" flag; clears session
  // flags so a fresh protectable cart auto-adds again.
  async removeProtectionIfNoProtectable(cart) {
    if (this.isLoading) return;
    if (!cart || !Array.isArray(cart.items)) return;

    const protectionItems = this.findAllProtectionItems(cart);
    if (protectionItems.length === 0) return;   // nothing to remove
    if (this.countProtectableItems(cart) > 0) return; // still valid

    this.setLoading(true);
    try {
      const freshCart = await FoxThemeCartHelpers.getState();
      const updates = this.buildRemovalUpdates(freshCart);
      if (Object.keys(updates).length === 0) {
        this.checkbox.checked = false;
        return;
      }
      const updatedCart = await this.postCartUpdate(updates);
      try {
        // Clear the auto-add flag so protection can auto-add
        // again if a protectable item is added later. Do NOT
        // clear the "user removed" flag here — a deliberate
        // decline should persist across this transition. (The
        // empty-cart path in autoAddIfFirstTime still resets it,
        // so fully emptying the cart is a clean fresh start.)
        sessionStorage.removeItem('uh_pp_auto_added');
      } catch (e) {}
      this.applyCartResponse(updatedCart);
    } catch (error) {
      console.error('[UH-PP] protectable cleanup failed:', error);
    } finally {
      this.setLoading(false);
    }
  }

  async autoAddIfFirstTime(cart) {
    if (this.isLoading) return;
    if (!cart || !Array.isArray(cart.items)) return;

    // Session key — auto-add only once per browser session
    const AUTO_ADD_KEY = 'uh_pp_auto_added';
    // Flag set when customer manually removes protection
    const REMOVED_KEY = 'uh_pp_user_removed';

    // If the cart has no real items (empty, or only the
    // protection product), reset the session flags so the next
    // cart starts fresh — a customer who emptied their cart and
    // shops again gets the normal auto-add behavior.
    const nonProtectionItems = cart.items.filter(
      (item) => item.product_id !== this.productId
    );
    if (nonProtectionItems.length === 0) {
      try {
        sessionStorage.removeItem(AUTO_ADD_KEY);
        sessionStorage.removeItem(REMOVED_KEY);
      } catch (e) {}
      return;
    }

    let alreadyAutoAdded = false;
    let userRemoved = false;
    try {
      alreadyAutoAdded =
        sessionStorage.getItem(AUTO_ADD_KEY) === 'true';
      userRemoved =
        sessionStorage.getItem(REMOVED_KEY) === 'true';
    } catch (e) {}

    // Never auto-add if we've done it this session
    // or the customer explicitly removed it
    if (alreadyAutoAdded || userRemoved) return;

    // Protection already in cart — nothing to do,
    // but mark as handled so we don't add later
    const protectionItems = this.findAllProtectionItems(cart);
    if (protectionItems.length > 0) {
      try {
        sessionStorage.setItem(AUTO_ADD_KEY, 'true');
      } catch (e) {}
      return;
    }

    // Only auto-add if the cart has at least one PROTECTABLE item.
    if (this.countProtectableItems(cart) === 0) return;

    // Mark BEFORE the async add to avoid double-fire
    try {
      sessionStorage.setItem(AUTO_ADD_KEY, 'true');
    } catch (e) {}

    await this.addProtection();
  }

  onCheckboxChange(event) {
    if (this.isLoading) {
      event.target.checked = !event.target.checked;
      return;
    }

    this.toggleProtection(event.target.checked);
  }

  async toggleProtection(shouldAdd) {
    const cart = await FoxThemeCartHelpers.getState();
    const protectionItems = this.findAllProtectionItems(cart);

    if (shouldAdd) {
      if (this.hasTargetVariant(cart)) return;

      if (protectionItems.length > 0) {
        await this.replaceWithTargetVariant();
        return;
      }

      await this.addProtection();
    } else {
      if (protectionItems.length === 0) return;
      await this.removeAllProtection();
    }
  }

  getSectionsToRender() {
    const sections = [];

    if (this.cartDrawer) {
      sections.push(
        ...this.cartDrawer
          .getSectionsToRender(true)
          .map((section) => section.id)
      );
    }

    if (this.mainCart) {
      sections.push(
        ...this.mainCart
          .getSectionsToRender()
          .map((section) => section.section)
      );
    }

    return sections;
  }

  setLoading(loading) {
    this.isLoading = loading;
    this.card.classList.toggle("is-loading", loading);
    this.checkbox.disabled = loading;
  }

  applyCartResponse(cart) {
    window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, cart);

    if (this.cartDrawer) {
      this.cartDrawer.renderContents(cart);
    }

    if (this.mainCart) {
      this.mainCart.renderContents(cart);
    }

    FoxThemeCartHelpers.updateCartCount(cart.item_count);
    this.syncFromCart(cart);
  }

  buildSectionsBody() {
    const body = {};
    const sections = this.getSectionsToRender();

    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }

    return body;
  }

  async postCartUpdate(updates) {
    const headers = new Headers({
      "Content-Type": "application/json",
      Accept: "application/json",
    });

    const response = await fetch(
      `${window.FoxThemeSettings.routes.cart_update_url}`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          updates,
          ...this.buildSectionsBody(),
        }),
      }
    );

    return response.json();
  }

  async postCartAdd() {
    const headers = new Headers({
      "Content-Type": "application/json",
      Accept: "application/json",
    });

    const response = await fetch(
      `${window.FoxThemeSettings.routes.cart_add_url}`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          items: [{ id: this.variantId, quantity: 1 }],
          ...this.buildSectionsBody(),
        }),
      }
    );

    return response.json();
  }

  buildRemovalUpdates(cart) {
    const updates = {};

    this.findAllProtectionItems(cart).forEach((item) => {
      updates[item.variant_id] = 0;
    });

    return updates;
  }

  async addProtection() {
    this.setLoading(true);

    try {
      const cart = await this.postCartAdd();

      if (cart.status) {
        this.checkbox.checked = false;
        console.error(cart.description || cart.message);
        return;
      }

      // Customer (or auto-add) added protection — clear
      // the removed flag so state is consistent
      try {
        sessionStorage.removeItem('uh_pp_user_removed');
      } catch (e) {}
      // /cart/add.js returns only the added line items, not
      // the full cart, so cart.item_count is unreliable and
      // the count badge briefly flickers (e.g. "99+"). Fetch
      // the authoritative cart and override item_count with it.
      let authoritativeCount = cart.item_count;
      try {
        const freshCart = await FoxThemeCartHelpers.getState();
        if (freshCart && typeof freshCart.item_count === 'number') {
          authoritativeCount = freshCart.item_count;
        }
      } catch (e) {}
      this.applyCartResponse({ ...cart, item_count: authoritativeCount });
    } catch (error) {
      this.checkbox.checked = false;
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  async removeAllProtection() {
    this.setLoading(true);

    try {
      const cart = await FoxThemeCartHelpers.getState();
      const updates = this.buildRemovalUpdates(cart);

      if (Object.keys(updates).length === 0) {
        this.checkbox.checked = false;
        return;
      }

      const updatedCart = await this.postCartUpdate(updates);
      // Remember the customer explicitly removed protection
      // so we never auto-add again this session
      try {
        sessionStorage.setItem('uh_pp_user_removed', 'true');
      } catch (e) {}
      this.applyCartResponse(updatedCart);
    } catch (error) {
      this.checkbox.checked = true;
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  async replaceWithTargetVariant() {
    this.setLoading(true);

    try {
      const cart = await FoxThemeCartHelpers.getState();
      const updates = this.buildRemovalUpdates(cart);

      if (Object.keys(updates).length > 0) {
        const clearedCart = await this.postCartUpdate(updates);

        if (clearedCart.status) {
          console.error(clearedCart.description || clearedCart.message);
          this.syncFromCart(clearedCart);
          return;
        }
      }

      const addedCart = await this.postCartAdd();

      if (addedCart.status) {
        console.error(addedCart.description || addedCart.message);
        this.syncFromCart(await FoxThemeCartHelpers.getState());
        return;
      }

      this.applyCartResponse(addedCart);
    } catch (error) {
      console.error(error);
      this.syncFromCart(await FoxThemeCartHelpers.getState());
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get("package-protection-component")) {
  customElements.define(
    "package-protection-component",
    PackageProtectionComponent
  );
}

const PACKAGE_PROTECTION_MODAL_ID = "PopupModal-package-protection";

let packageProtectionModalFromDrawer = false;

function isCartDrawerProtectionTrigger(element) {
  return Boolean(
    element &&
      element.closest(
        "#cart-drawer, cart-drawer, .f-cart-drawer, #Drawer-Cart, drawer-component.f-drawer-cart"
      )
  );
}

function hideCartDrawerScrollHint() {
  if (
    window.FoxThemeCartScroll &&
    typeof window.FoxThemeCartScroll.hideHint === "function"
  ) {
    window.FoxThemeCartScroll.hideHint();
  }
}

function restoreCartDrawerScrollHint() {
  if (
    window.FoxThemeCartScroll &&
    typeof window.FoxThemeCartScroll.updateHint === "function"
  ) {
    window.FoxThemeCartScroll.updateHint();
  }
}

function bindPackageProtectionModalClose(modal) {
  if (!modal || modal._ppCloseBound) return;
  modal._ppCloseBound = true;

  modal.addEventListener("close", () => {
    if (!packageProtectionModalFromDrawer) return;

    document.body.classList.remove("modal-open");
    packageProtectionModalFromDrawer = false;
    setTimeout(restoreCartDrawerScrollHint, 350);
  });
}

function ensurePackageProtectionModalCloseBinding() {
  const modal = document.getElementById(PACKAGE_PROTECTION_MODAL_ID);
  if (modal) {
    bindPackageProtectionModalClose(modal);
    return;
  }

  if (window.__ppModalCloseObserver) return;

  window.__ppModalCloseObserver = new MutationObserver(() => {
    const nextModal = document.getElementById(PACKAGE_PROTECTION_MODAL_ID);
    if (nextModal) {
      bindPackageProtectionModalClose(nextModal);
      window.__ppModalCloseObserver.disconnect();
      window.__ppModalCloseObserver = null;
    }
  });

  window.__ppModalCloseObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function openPackageProtectionModal(opener) {
  const modal = document.getElementById(PACKAGE_PROTECTION_MODAL_ID);
  if (!modal || typeof modal.show !== "function") return;

  ensurePackageProtectionModalCloseBinding();
  bindPackageProtectionModalClose(modal);

  packageProtectionModalFromDrawer = isCartDrawerProtectionTrigger(opener);

  if (packageProtectionModalFromDrawer) {
    hideCartDrawerScrollHint();
    document.body.classList.add("modal-open");
  }

  modal.show(opener);
}

if (!window.__packageProtectionModalBound) {
  window.__packageProtectionModalBound = true;

  ensurePackageProtectionModalCloseBinding();

  document.addEventListener(
    "click",
    (event) => {
      const button = event.target.closest(
        "[data-package-protection-modal-open], .f-package-protection__info-btn"
      );
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();
      openPackageProtectionModal(button);
    },
    true
  );
}
